import { useEffect, useRef, useState } from "react";
import { Col, Form, FormFeedback, Input, Label, Row } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useProfile } from "features/Authentication/hooks/useProfile";
import {
  getEmployee,
  updateMyProfile,
} from "features/Employees/services/EmployeeService";
import { Employee } from "features/Employees/types/employee.types";
import PageLoadingSpinner from "components/common/PageLoader";
import Avatar from "components/common/Avatar";
import { updateAuthUser } from "features/Authentication/store/login/reducer";

const schema = Yup.object({
  firstName: Yup.string().nullable().optional(),
  lastName: Yup.string().nullable().optional(),
  phone: Yup.string().nullable().optional(),
  address: Yup.string().nullable().optional(),
  city: Yup.string().nullable().optional(),
  state: Yup.string().nullable().optional(),
  pincode: Yup.string().nullable().optional(),
  bloodGroup: Yup.string().nullable().optional(),
});

function strOrNull(s: string | undefined | null): string | null {
  const t = (s ?? "").trim();
  return t === "" ? null : t;
}

const EmployeeProfileTab = () => {
  const dispatch = useDispatch();
  const { user: profileUser } = useProfile();
  const employeeId = profileUser?.employee?.id;

  const [record, setRecord] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(true);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [removeAvatar, setRemoveAvatar] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!employeeId) {
      setLoading(false);
      return;
    }
    getEmployee(employeeId)
      .then((res) => {
        if (res.success) setRecord(res.data);
      })
      .catch(console.warn)
      .finally(() => setLoading(false));
  }, [employeeId]);

  useEffect(() => {
    return () => {
      if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    };
  }, [avatarPreview]);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type)) {
      toast.error("Only PNG, JPG, and JPEG images are accepted.");
      e.target.value = "";
      return;
    }
    setAvatarFile(file);
    setAvatarError(null);
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarPreview(URL.createObjectURL(file));
    setRemoveAvatar(false);
  };

  const handleRemoveAvatar = () => {
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarFile(null);
    setAvatarPreview(null);
    setRemoveAvatar(true);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: record?.user?.firstName ?? record?.firstName ?? "",
      lastName: record?.user?.lastName ?? record?.lastName ?? "",
      phone: record?.user?.phone ?? record?.phone ?? "",
      address: record?.address ?? "",
      city: record?.city ?? "",
      state: record?.state ?? "",
      pincode: record?.pincode ?? "",
      bloodGroup: record?.bloodGroup ?? "",
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      if (!record) return;
      try {
        let response;
        const textFields = {
          firstName: strOrNull(values.firstName),
          lastName: strOrNull(values.lastName),
          phone: strOrNull(values.phone),
          address: strOrNull(values.address),
          city: strOrNull(values.city),
          state: strOrNull(values.state),
          pincode: strOrNull(values.pincode),
          bloodGroup: strOrNull(values.bloodGroup),
        };
        if (avatarFile) {
          const fd = new FormData();
          for (const [key, val] of Object.entries(textFields)) {
            if (val !== null) fd.append(key, val);
          }
          fd.append("avatar", avatarFile);
          response = await updateMyProfile(fd);
        } else if (removeAvatar) {
          const fd = new FormData();
          for (const [key, val] of Object.entries(textFields)) {
            if (val !== null) fd.append(key, val);
          }
          fd.append("removeAvatar", "true");
          response = await updateMyProfile(fd);
        } else {
          response = await updateMyProfile(textFields);
        }
        if (response.success) {
          toast.success("Profile updated successfully");
          setRecord(response.data);
          setAvatarFile(null);
          setAvatarPreview(null);
          setRemoveAvatar(false);
          const updatedUser = response.data?.user;
          if (updatedUser) {
            dispatch(
              updateAuthUser({
                firstName: updatedUser.firstName,
                lastName: updatedUser.lastName,
                avatar: updatedUser.avatar,
              }),
            );
          }
        }
      } catch (err: any) {
        const msg = err?.response?.data?.message ?? "Something went wrong";
        toast.error(msg);
        if (avatarFile) setAvatarError(msg);
      }
    },
  });

  if (loading) return <PageLoadingSpinner message="Loading your profile..." />;

  if (!record) {
    return (
      <p className="text-muted mb-0">
        No employee profile is linked to your account.
      </p>
    );
  }

  const { values, touched, errors, handleChange, handleBlur, isSubmitting } =
    formik;

  const displayName =
    `${values.firstName} ${values.lastName}`.trim() ||
    record.user?.email ||
    record.email;

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        formik.handleSubmit();
      }}
    >
      <div className="d-flex align-items-center gap-3 mb-4 pb-3 border-bottom">
        <div
          className="avatar flex-shrink-0"
          style={{ width: 64, height: 64 }}
        >
          <Avatar
            name={displayName}
            image={removeAvatar ? undefined : (avatarPreview ?? record.user?.avatar ?? record.avatar ?? undefined)}
            lightbox
          />
        </div>
        <div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button
              type="button"
              className="btn btn-soft-primary btn-sm"
              onClick={() => fileInputRef.current?.click()}
            >
              <i className="ri-upload-2-line me-1" />
              Change photo
            </button>
            {(avatarPreview || record.user?.avatar || record.avatar) && !removeAvatar ? (
              <button
                type="button"
                className="btn btn-soft-danger btn-sm"
                onClick={handleRemoveAvatar}
              >
                <i className="ri-delete-bin-line me-1" />
                Remove photo
              </button>
            ) : null}
          </div>
          <p className="text-muted small mb-0 mt-1">PNG, JPG, or JPEG only.</p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".jpg,.jpeg,.png"
          className="d-none"
          onChange={handleAvatarChange}
        />
        {avatarError ? (
          <p className="text-danger small mt-1 mb-0">{avatarError}</p>
        ) : null}
      </div>

      <Row>
        <Col md={6} className="mb-3">
          <Label className="form-label">First name</Label>
          <Input
            type="text"
            name="firstName"
            placeholder="First name"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.firstName || ""}
            invalid={touched.firstName && !!errors.firstName}
          />
          {touched.firstName && errors.firstName ? (
            <FormFeedback>{errors.firstName}</FormFeedback>
          ) : null}
        </Col>
        <Col md={6} className="mb-3">
          <Label className="form-label">Last name</Label>
          <Input
            type="text"
            name="lastName"
            placeholder="Last name"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.lastName || ""}
            invalid={touched.lastName && !!errors.lastName}
          />
          {touched.lastName && errors.lastName ? (
            <FormFeedback>{errors.lastName}</FormFeedback>
          ) : null}
        </Col>
        <Col md={6} className="mb-3">
          <Label className="form-label">Phone</Label>
          <Input
            type="text"
            name="phone"
            placeholder="Phone number"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.phone || ""}
            invalid={touched.phone && !!errors.phone}
          />
          {touched.phone && errors.phone ? (
            <FormFeedback>{errors.phone}</FormFeedback>
          ) : null}
        </Col>
        <Col md={6} className="mb-3">
          <Label className="form-label">Blood group</Label>
          <Input
            type="text"
            name="bloodGroup"
            placeholder="e.g. A+, B-, O+"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.bloodGroup || ""}
            invalid={touched.bloodGroup && !!errors.bloodGroup}
          />
          {touched.bloodGroup && errors.bloodGroup ? (
            <FormFeedback>{errors.bloodGroup}</FormFeedback>
          ) : null}
        </Col>
        <Col md={12} className="mb-3">
          <Label className="form-label">Address</Label>
          <Input
            type="textarea"
            name="address"
            rows={2}
            placeholder="Street address"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.address || ""}
            invalid={touched.address && !!errors.address}
          />
          {touched.address && errors.address ? (
            <FormFeedback>{errors.address}</FormFeedback>
          ) : null}
        </Col>
        <Col md={6} className="mb-3">
          <Label className="form-label">City</Label>
          <Input
            type="text"
            name="city"
            placeholder="City"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.city || ""}
            invalid={touched.city && !!errors.city}
          />
          {touched.city && errors.city ? (
            <FormFeedback>{errors.city}</FormFeedback>
          ) : null}
        </Col>
        <Col md={4} className="mb-3">
          <Label className="form-label">State</Label>
          <Input
            type="text"
            name="state"
            placeholder="State"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.state || ""}
            invalid={touched.state && !!errors.state}
          />
          {touched.state && errors.state ? (
            <FormFeedback>{errors.state}</FormFeedback>
          ) : null}
        </Col>
        <Col md={2} className="mb-3">
          <Label className="form-label">Pincode</Label>
          <Input
            type="text"
            name="pincode"
            placeholder="Pincode"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.pincode || ""}
            invalid={touched.pincode && !!errors.pincode}
          />
          {touched.pincode && errors.pincode ? (
            <FormFeedback>{errors.pincode}</FormFeedback>
          ) : null}
        </Col>
      </Row>
      <div className="d-flex justify-content-end mt-2">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Saving..." : "Save changes"}
        </button>
      </div>
    </Form>
  );
};

export default EmployeeProfileTab;
